import { useEffect, useState } from "react";
import { Share, SquarePlus, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isIOS, isMobileDevice, isStandalone, onInstallAvailable, promptNativeInstall } from "@/lib/pwa";

export const InstallPrompt = () => {
  const { user, updateUser } = useAuth();
  const [nativeAvailable, setNativeAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const eligible = Boolean(user) && !user.install_prompt_seen_at && !user.password_change_required && !user.policy_acceptance_required && !user.two_factor_setup_required;

  useEffect(() => onInstallAvailable(setNativeAvailable), []);
  useEffect(() => {
    if (!eligible || isStandalone() || !isMobileDevice()) return undefined;
    const timer = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(timer);
  }, [eligible]);

  if (!open) return null;

  async function done(outcome) {
    setOpen(false);
    updateUser?.({ install_prompt_seen_at: new Date().toISOString() });
    api.post("/push/install-prompt-seen", { outcome }).catch(() => undefined);
  }

  async function install() {
    const outcome = await promptNativeInstall();
    done(outcome);
  }

  const ios = isIOS();
  return (
    <div className="install-prompt" role="dialog" aria-label="Install TAGForce" data-testid="install-prompt">
      <button className="install-prompt-close" type="button" aria-label="Not now" data-testid="install-prompt-dismiss" onClick={() => done("dismissed")}><X size={16} /></button>
      <div className="install-prompt-head">
        <img src="/logo192.png" alt="" width="48" height="48" />
        <div>
          <strong>Install TAGForce</strong>
          <span>Full-screen app on your home screen, with notifications for reports, approvals and assignment changes.</span>
        </div>
      </div>
      {ios ? (
        <ol className="install-prompt-steps" data-testid="install-prompt-ios-steps">
          <li><Share size={15} /> Tap <b>Share</b> in Safari's toolbar</li>
          <li><SquarePlus size={15} /> Choose <b>Add to Home Screen</b></li>
          <li>Tap <b>Add</b>, then open TAGForce from your home screen</li>
        </ol>
      ) : (
        <p className="install-prompt-steps" data-testid="install-prompt-android-steps">{nativeAvailable ? "Tap Install to add TAGForce to your home screen." : "Open your browser menu (⋮) and choose Install app or Add to Home screen."}</p>
      )}
      <div className="install-prompt-actions">
        <button className="button button-secondary" type="button" data-testid="install-prompt-later" onClick={() => done("dismissed")}>Not now</button>
        {!ios && nativeAvailable ? (
          <button className="button button-primary" type="button" data-testid="install-prompt-install" onClick={install}>Install</button>
        ) : (
          <button className="button button-primary" type="button" data-testid="install-prompt-got-it" onClick={() => done("instructions")}>Got it</button>
        )}
      </div>
    </div>
  );
};
